"use client";

import { useState } from "react";
import { useSnackbar } from "notistack";
import { useAuth } from "@/src/core/auth/useAuth";
import { errorMessage } from "@/src/core/api/validation";
import { useReportReasonsQuery, useSubmitReportMutation } from "../api/queries";

const THANK_YOU = "Thanks for letting us know. We'll look into it.";
const FAILED = "Something went wrong, try again!";

/**
 * "Report this listing", as the app's report screen: pick a reason (some need a short
 * description), then submit. Submitting asks a signed-out guest to log in first.
 */
export const useReportListing = (listingId: number) => {
  const { isAuthenticated, login } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const [isOpen, setIsOpen] = useState(false);
  const [reasonId, setReasonId] = useState<number | null>(null);
  const [details, setDetails] = useState("");
  const reasonsQuery = useReportReasonsQuery(isOpen);
  const submitMutation = useSubmitReportMutation();

  const reasons = reasonsQuery.data ?? [];
  const reason = reasons.find((item) => item.id === reasonId) ?? null;
  const canSubmit =
    !!reason && (!reason.requires_details || details.trim().length > 0);

  const close = () => {
    setIsOpen(false);
    setReasonId(null);
    setDetails("");
  };

  const submit = async () => {
    if (!canSubmit || !reason || submitMutation.isPending) return;
    if (!isAuthenticated) {
      login();
      return;
    }
    try {
      await submitMutation.mutateAsync({
        target_type: "listing",
        target_id: listingId,
        reason_id: reason.id,
        ...(details.trim() ? { details: details.trim() } : {}),
      });
      close();
      enqueueSnackbar(THANK_YOU, { variant: "default" });
    } catch (error) {
      enqueueSnackbar(errorMessage(error, FAILED), { variant: "error" });
    }
  };

  return {
    isOpen,
    open: () => setIsOpen(true),
    close,
    reasons,
    isLoadingReasons: reasonsQuery.isLoading,
    reasonsError: reasonsQuery.isError,
    retryReasons: () => void reasonsQuery.refetch(),
    reasonId,
    selectReason: setReasonId,
    /** The chosen reason asks for a description. */
    needsDetails: !!reason?.requires_details,
    details,
    setDetails,
    canSubmit,
    isSubmitting: submitMutation.isPending,
    submit,
  };
};
